import { observer } from 'mobx-react';

import { Navigate, Route, Routes } from 'react-router-dom';

import { useStores } from 'mobx/hooks/useStores';

import { FEATURES } from 'constants/features';

import { useHasAnalyticsFeatures } from 'hooks/useFeature';

import PatientsActivation from 'views/Pages/Activation/PatientsActivation';
import AnalyticsDashboardPage from 'views/Pages/AnalyticsDashboard/AnalyticsDashboardPage';
import CareManagementPage from 'views/Pages/CareManagement/CareManagementPage';
import CreateTicketsPage from 'views/Pages/CreateTickets/CreateTickets';
import Invitation from 'views/Pages/Invitation/Invitation';
import { PathwayBuilderPage } from 'views/Pages/PathwayBuilder/PathwayBuilderPage';
import { PatientList } from 'views/Pages/PatientList/PatientList';
import { PatientRegistrationPage } from 'views/Pages/PatientRegistration/PatientRegistrationPage';
import PracticeManagerPage from 'views/Pages/PracticeManagement/PracticeManagerPage';

import ClinicianAlertsPage from 'views/Patient/Alerts/ClinicianAlerts';
import PatientMainView from 'views/Patient/PatientMain/PatientMainView';

import { WorkQueuePage } from 'views/WorkQueue/WorkQueuePage';

import PrivateRoute from 'components/PrivateRoute';

const PrivateRoutes = () => {
  const { settingsStore, userStore } = useStores();
  const hasAnalyticsFeatures = useHasAnalyticsFeatures();

  //We have a scenario where user does not have the Work Queue feature - in this case we will show him an empty page.
  const shouldAllowEmptyPage =
    userStore.isAuthenticated && !settingsStore.hasFeature(FEATURES.WORK_QUEUE_PAGE);

  const getRedirectRoute = () => {
    if (settingsStore.hasFeature(FEATURES.WORK_QUEUE_PAGE)) {
      return '/work-queue';
    }

    if (shouldAllowEmptyPage) {
      return '/home';
    }

    return '/';
  };

  const redirectRoute = getRedirectRoute();

  return (
    <Routes>
      <Route
        path="/home"
        element={
          <PrivateRoute
            element={<div data-test-hook="empty-page" />}
            redirectRoute={redirectRoute}
            shouldAllow={shouldAllowEmptyPage}
          />
        }
      />

      <Route
        path="/triage"
        element={
          <PrivateRoute
            element={<WorkQueuePage />}
            redirectRoute={redirectRoute}
            shouldAllow={settingsStore.hasFeature(FEATURES.WORK_QUEUE_PAGE)}
          />
        }
      />

      <Route
        path="/work-queue/:activeTab?"
        element={
          <PrivateRoute
            element={<WorkQueuePage />}
            redirectRoute={redirectRoute}
            shouldAllow={settingsStore.hasFeature(FEATURES.WORK_QUEUE_PAGE)}
          />
        }
      />

      <Route
        path="/patient/:patientId/*"
        element={
          <PrivateRoute
            element={<PatientMainView />}
            redirectRoute={redirectRoute}
            shouldAllow={settingsStore.hasFeature(FEATURES.PATIENT_PAGE)}
          />
        }
      />

      <Route
        path="/alerts"
        element={
          <PrivateRoute
            element={<ClinicianAlertsPage />}
            redirectRoute={redirectRoute}
            shouldAllow={settingsStore.hasFeature(FEATURES.TRIAGE_RULES)}
          />
        }
      />

      <Route
        path="/invitation"
        element={
          <PrivateRoute
            element={<Invitation />}
            redirectRoute={redirectRoute}
            shouldAllow={settingsStore.hasFeature(FEATURES.INVITE_PATIENTS)}
          />
        }
      />

      <Route
        path="/create-tickets"
        element={
          <PrivateRoute
            element={<CreateTicketsPage />}
            redirectRoute={redirectRoute}
            shouldAllow={settingsStore.hasFeature(FEATURES.CREATE_TICKET)}
          />
        }
      />

      <Route
        path="/care-management/:activeTab"
        element={
          <PrivateRoute
            element={<CareManagementPage />}
            redirectRoute={redirectRoute}
            shouldAllow={settingsStore.hasFeature(FEATURES.CARE_MANAGEMENT)}
          />
        }
      />

      <Route
        path="/practice-analytics"
        element={
          <PrivateRoute
            element={<AnalyticsDashboardPage />}
            redirectRoute={redirectRoute}
            shouldAllow={hasAnalyticsFeatures}
          />
        }
      />

      <Route
        path="/practice/:activeTab"
        element={
          <PrivateRoute
            element={<PracticeManagerPage />}
            redirectRoute={redirectRoute}
            shouldAllow={userStore.isManager && settingsStore.hasFeature(FEATURES.PRACTICE_MANAGER)}
          />
        }
      />

      <Route
        path="/pathway-builder/:activeTab"
        element={
          <PrivateRoute
            element={<PathwayBuilderPage />}
            redirectRoute={redirectRoute}
            shouldAllow={userStore.isAdmin}
          />
        }
      />

      <Route
        path="/activation"
        element={
          <PrivateRoute
            element={<PatientsActivation />}
            redirectRoute={redirectRoute}
            shouldAllow={settingsStore.hasFeature(FEATURES.AWAITING_ACTIVATION)}
          />
        }
      />

      <Route
        path="/patient-list"
        element={
          <PrivateRoute
            element={<PatientList />}
            redirectRoute={redirectRoute}
            shouldAllow={userStore.isAdmin}
          />
        }
      />

      <Route
        path="/patient-registration"
        element={
          <PrivateRoute
            element={<PatientRegistrationPage />}
            redirectRoute={redirectRoute}
            shouldAllow={settingsStore.hasFeature(FEATURES.PATIENT_REGISTRATION)}
          />
        }
      />

      <Route path="/" element={<Navigate to={redirectRoute} replace />} />
      <Route path="*" element={<Navigate to={redirectRoute} replace />} />
    </Routes>
  );
};

export default observer(PrivateRoutes);
